import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faArrowLeft,
  faTimes,
  faEye,
  faDownload,
  faUpload,
  faFileUpload,
} from "@fortawesome/free-solid-svg-icons";
import {
  getProcedureServiceById,
  createProcedureService,
  updateProcedureService,
} from "../../../services/api";
import { ProcedureService } from "../../../types/types";
import { toast } from "react-toastify";
import AdminLayout from "../AdminLayout";
import { useLanguage } from "../../../context/LanguageContext";
import { getTranslation } from "../../../utils/translations";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { getFileName } from "../../../utils/media";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    [{ color: [] }, { background: [] }],
    ["clean"],
  ],
};

interface FileWithPreview {
  file: File;
  preview?: string;
  type: "image" | "document";
}

const ProcedureForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [imageFile, setImageFile] = useState<FileWithPreview | null>(null);
  const [documentFile, setDocumentFile] = useState<FileWithPreview | null>(
    null,
  );
  const [existingImage, setExistingImage] = useState<string | null>(null);
  const [existingDocument, setExistingDocument] = useState<string | null>(null);
  const [arabicText, setArabicText] = useState("");
  const [frenchText, setFrenchText] = useState("");
  const [isImageDragOver, setIsImageDragOver] = useState(false);
  const [isDocDragOver, setIsDocDragOver] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProcedureService>();

  useEffect(() => {
    if (isEditMode) {
      const fetchProcedure = async () => {
        try {
          setIsLoading(true);
          const response = await getProcedureServiceById(parseInt(id!));
          const procedure = response.data;

          reset({
            ...procedure,
          });
          setArabicText(procedure.textarabe || "");
          setFrenchText(procedure.textfrancais || "");

          if (procedure.image) {
            setExistingImage(procedure.image);
          }
          if (procedure.fichier) {
            setExistingDocument(procedure.fichier);
          }
        } catch (error) {
          console.error("Error fetching procedure:", error);
          toast.error(getTranslation("fetchError", language));
        } finally {
          setIsLoading(false);
        }
      };
      fetchProcedure();
    }
  }, [id, isEditMode, reset]);

  const handleFileDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>, type: "image" | "document") => {
      event.preventDefault();
      const files = event.dataTransfer.files;
      if (files && files[0]) {
        const file = files[0];

        if (type === "image" && file.type.startsWith("image/")) {
          const fileWithPreview: FileWithPreview = {
            file,
            preview: URL.createObjectURL(file),
            type: "image",
          };
          setImageFile(fileWithPreview);
        } else if (
          type === "document" &&
          (file.type === "application/pdf" ||
            file.type === "application/msword" ||
            file.type ===
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
        ) {
          const fileWithPreview: FileWithPreview = {
            file,
            type: "document",
          };
          setDocumentFile(fileWithPreview);
        }
      }

      if (type === "image") setIsImageDragOver(false);
      else setIsDocDragOver(false);
    },
    [],
  );

  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "image" | "document",
  ) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (type === "image" && file.type.startsWith("image/")) {
        const fileWithPreview: FileWithPreview = {
          file,
          preview: URL.createObjectURL(file),
          type: "image",
        };
        setImageFile(fileWithPreview);
      } else if (
        type === "document" &&
        (file.type === "application/pdf" ||
          file.type === "application/msword" ||
          file.type ===
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
      ) {
        const fileWithPreview: FileWithPreview = {
          file,
          type: "document",
        };
        setDocumentFile(fileWithPreview);
      }
    }
  };

  const removeFile = (type: "image" | "document") => {
    if (type === "image") {
      if (imageFile?.preview) URL.revokeObjectURL(imageFile.preview);
      setImageFile(null);
    } else {
      setDocumentFile(null);
    }
  };

  const encodeFileContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const onSubmit = async (data: ProcedureService) => {
    setIsLoading(true);
    try {
      let imageValue: string | null = null;
      let documentValue: string | null = null;

      if (imageFile) {
        imageValue = await encodeFileContent(imageFile.file);
      } else if (existingImage && isEditMode) {
        imageValue = getFileName(existingImage) || existingImage;
      }

      if (documentFile) {
        documentValue = await encodeFileContent(documentFile.file);
      } else if (existingDocument && isEditMode) {
        documentValue = getFileName(existingDocument) || existingDocument;
      }

      const payload = {
        titrearabe: data.titrearabe,
        titrefrancais: data.titrefrancais,
        textarabe: arabicText,
        textfrancais: frenchText,
        image: imageValue,
        fichier: documentValue,
      };

      if (isEditMode) {
        await updateProcedureService(parseInt(id!), payload);
        toast.success(getTranslation("updateSuccess", language));
      } else {
        await createProcedureService(payload);
        toast.success(getTranslation("saveSuccess", language));
      }

      navigate("/admin/procedures");
    } catch (error) {
      console.error("Error saving procedure:", error);
      toast.error(getTranslation("saveError", language));
    } finally {
      setIsLoading(false);
    }
  };

  // Clean up object URLs
  useEffect(() => {
    return () => {
      if (imageFile?.preview) {
        URL.revokeObjectURL(imageFile.preview);
      }
    };
  }, [imageFile]);

  return (
    <AdminLayout>
      <style>
        {`
          .rtl-quill .ql-editor { direction: rtl; text-align: right; }
          .ltr-quill .ql-editor { direction: ltr; text-align: left; }
        `}
      </style>

      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-mauritania-green-dark">
          {isEditMode
            ? getTranslation("editProcedureTitle", language)
            : getTranslation("addProcedureTitle", language)}
        </h1>
        <button
          onClick={() => navigate("/admin/procedures")}
          className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition duration-300"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="ml-2" />
          {getTranslation("back", language)}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Titles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="titrearabe">
                {getTranslation("procedureTitleArabic", language)} *
              </label>
              <input
                id="titrearabe"
                type="text"
                {...register("titrearabe", {
                  required: getTranslation("requiredField", language),
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.titrearabe ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.titrearabe && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.titrearabe.message}
                </p>
              )}
            </div>

            <div>
              <label
                className="block text-gray-700 mb-2"
                htmlFor="titrefrancais"
              >
                {getTranslation("procedureTitleFrench", language)} *
              </label>
              <input
                id="titrefrancais"
                type="text"
                {...register("titrefrancais", {
                  required: getTranslation("requiredField", language),
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.titrefrancais ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.titrefrancais && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.titrefrancais.message}
                </p>
              )}
            </div>
          </div>

          {/* Arabic text */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">
              {getTranslation("procedureFormArabicDescription", language)}
            </label>
            <div className="rtl-quill">
              <ReactQuill
                theme="snow"
                value={arabicText}
                onChange={setArabicText}
                modules={quillModules}
                className="h-64 mb-16"
              />
            </div>
          </div>

          {/* French text */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">
              {getTranslation("procedureFormFrenchDescription", language)}
            </label>
            <div className="ltr-quill">
              <ReactQuill
                theme="snow"
                value={frenchText}
                onChange={setFrenchText}
                modules={quillModules}
                className="h-64 mb-16"
              />
            </div>
          </div>

          {/* Files Upload Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Image Upload */}
            <div>
              <label className="block text-gray-700 mb-2">الصورة</label>
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors h-48 flex items-center justify-center ${
                  isImageDragOver
                    ? "border-mauritania-green bg-mauritania-green-light"
                    : "border-gray-300 hover:border-mauritania-green"
                }`}
                onDrop={(e) => handleFileDrop(e, "image")}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsImageDragOver(true);
                }}
                onDragLeave={() => setIsImageDragOver(false)}
                onClick={() => document.getElementById("image-input")?.click()}
              >
                <input
                  id="image-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect(e, "image")}
                  className="hidden"
                />

                {imageFile ? (
                  <div className="relative">
                    <img
                      src={imageFile.preview}
                      alt="Preview"
                      className="h-32 object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile("image");
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <FontAwesomeIcon icon={faTimes} className="text-xs" />
                    </button>
                  </div>
                ) : existingImage ? (
                  <div className="text-center">
                    <div className="bg-green-100 p-3 rounded-lg inline-block mb-3">
                      <FontAwesomeIcon
                        icon={faEye}
                        className="text-green-600"
                      />
                    </div>
                    <p className="text-sm text-green-700">صورة موجودة</p>
                  </div>
                ) : (
                  <div>
                    <div className="bg-gray-100 p-3 rounded-lg inline-block mb-3">
                      <FontAwesomeIcon
                        icon={faUpload}
                        className="text-gray-400 text-2xl"
                      />
                    </div>
                    <p className="text-sm text-gray-600">
                      اسحب الصورة هنا أو انقر للاختيار
                    </p>
                    <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF</p>
                  </div>
                )}
              </div>
            </div>

            {/* Document Upload */}
            <div>
              <label className="block text-gray-700 mb-2">الملف المرفق</label>
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors h-48 flex items-center justify-center ${
                  isDocDragOver
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-blue-500"
                }`}
                onDrop={(e) => handleFileDrop(e, "document")}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDocDragOver(true);
                }}
                onDragLeave={() => setIsDocDragOver(false)}
                onClick={() =>
                  document.getElementById("document-input")?.click()
                }
              >
                <input
                  id="document-input"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileSelect(e, "document")}
                  className="hidden"
                />

                {documentFile ? (
                  <div className="text-center">
                    <div className="bg-blue-100 p-3 rounded-lg inline-block mb-3">
                      <FontAwesomeIcon
                        icon={faFileUpload}
                        className="text-blue-600 text-2xl"
                      />
                    </div>
                    <p className="text-sm font-medium text-gray-700">
                      {documentFile.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(documentFile.file.size / 1024).toFixed(1)} KB
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile("document");
                      }}
                      className="text-red-600 hover:text-red-800 text-sm mt-2"
                    >
                      <FontAwesomeIcon icon={faTimes} className="ml-1" /> إزالة
                    </button>
                  </div>
                ) : existingDocument ? (
                  <div className="text-center">
                    <div className="bg-green-100 p-3 rounded-lg inline-block mb-3">
                      <FontAwesomeIcon
                        icon={faFileUpload}
                        className="text-green-600"
                      />
                    </div>
                    <p className="text-sm text-green-700">ملف موجود</p>
                  </div>
                ) : (
                  <div>
                    <div className="bg-gray-100 p-3 rounded-lg inline-block mb-3">
                      <FontAwesomeIcon
                        icon={faFileUpload}
                        className="text-gray-400 text-2xl"
                      />
                    </div>
                    <p className="text-sm text-gray-600">
                      اسحب الملف هنا أو انقر للاختيار
                    </p>
                    <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="bg-mauritania-green text-white py-2 px-6 rounded-md hover:bg-mauritania-green-dark transition duration-300 focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:ring-opacity-50 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faSave} className="ml-2" />
              {isLoading ? getTranslation("saving", language) : getTranslation("save", language)}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default ProcedureForm;
